// @flow

import { Fragment, useContext, useState, useEffect } from 'react';
import { Alert, Intent } from '@blueprintjs/core';
import { authPkgConfig } from '@auravisionlabs/aura-pkg-auth-service';
import * as UserModel from '../../models/user';
import * as ReportModel from '../../models/report';
import DesktopSidebar from './desktop';
import MobileSidebar from './mobile';
import {
  SUMMARY,
  HEATMAPS,
  EXPLORER,
  LIVE_OCC,
  REPORTS,
  SINGLE_REPORT,
  MY_REPORTS,
  SINGLE_MY_REPORT,
  HOME_SCREEN,
  AI_CHAT,
  AI_REPORTS,
  AI_REPORTS_EDIT,
  CAMERA_PREVIEW,
  TRAINING,
  SETTINGS_USERS,
  SETTINGS_MANAGE_LOCATIONS,
  SETTINGS_TOOLS,
  HEADCOUNT_PLANNER,
  SETTINGS_MANAGE_SINGLE_LOCATION,
  SETTINGS_MANAGE_SINGLE_RECORDING,
  CAMPAIGNS,
} from '../../constants/routes';
import { Desktop, Mobile } from '../layouts/devices-sizes';
import { errorToast, successToast } from '../../utils/toaster';
import { validateThreadTitle } from '../../utils/chat-title';
import { REPORT_NAME_MAX_LEN } from '../ai-reports/ui-model';
import {
  getAuthPkgUser,
  initAuthPkgUser,
} from '../../services/auth-pkg-user';
import { ThemeContext } from 'styled-components';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { withRouter } from 'react-router-dom';

type Props = {
  user: UserModel.t,
  showLivePage: boolean,
  showDemoLivePage: boolean,
  showHeadcountPlanner: Boolean,
  canViewUsers: boolean,
  logout: (options: Object) => void | Promise<Object>,
  orgReports?: ReportModel.t[],
  myReports?: ReportModel.t[],
  chatThreads?: Array<{
    id: string,
    title: string | null,
    createdAt: string,
    updatedAt: string,
    pinned: boolean,
    pinnedDate: number | null,
  }>,
  updateChatThread: Function,
  deleteChatThread: Function,
  updateDashboardReport: Function,
  deleteDashboardReport: Function,
  location: Object,
  history: Object,
};

type MenuItemT = {
  label: string,
  icon?: string,
  path: string,
  action?: { icon: string, tooltip: string, path: string },
  children?: MenuItemT[],

};

const Sidebar: MenuItemT = (props: Props) => {
  // Only one row can have an open context menu or be in inline edit at once (D6).
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [orgReportPerms, setOrgReportPerms] = useState({
    canUpdate: false,
    canDelete: false,
  });

  // Org-report permissions (R15/D12): evaluated once on mount, false-on-error /
  // until resolved. My Reports items never depend on these.
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        let authUser = getAuthPkgUser();
        if (!authUser) {
          authUser = await initAuthPkgUser();
        }
        if (!authUser) {
          if (mounted) setOrgReportPerms({ canUpdate: false, canDelete: false });
          return;
        }
        const orgId = await authUser.getPrimaryOrgId();
        const { resources, authActions } = authPkgConfig;
        const [upd, del] = await Promise.all([
          authUser
            .can(resources.dashboardReport, authActions.update, {
              orgIds: [orgId],
            })
            .evaluate(),
          authUser
            .can(resources.dashboardReport, authActions.delete, {
              orgIds: [orgId],
            })
            .evaluate(),
        ]);
        if (mounted) setOrgReportPerms({ canUpdate: !!upd, canDelete: !!del });
      } catch (e) {
        if (mounted) setOrgReportPerms({ canUpdate: false, canDelete: false });
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Navigation cancels any open context menu or in-progress edit (D6/D7).
  useEffect(() => {
    setMenuOpenId(null);
    setEditingId(null);
  }, [props.location.pathname]);

  const home: MenuItemT = {
    label: 'Home',
    icon: 'home',
    path: HOME_SCREEN,
  };

  const keyMetrics = {
    label: 'Key Metrics',
    icon: 'dashboard',
    path: SUMMARY,
  };

  const aiChat: MenuItemT = {
    label: 'Ask Aura',
    icon: 'chat',
    path: AI_CHAT,
    exactMatch: true,
  };

  const chatThreads = props.chatThreads || [];

  // API-enforced pin limit (see aura-api pinning/rename doc §2). A fresh pin
  // over the limit returns 409; we also pre-check locally to save the call.
  const MAX_PINNED_CHATS = 30;
  const PIN_LIMIT_MESSAGE =
    'Pin limit reached (30). Unpin an existing chat to pin more.';
  const pinnedCount = chatThreads.filter((t) => t && t.pinned).length;

  // chatThreads arrives pre-sorted pinned-first (getSortedChatThreads selector).
  const buildThreadItem = (thread) => {
    const title = thread.title || 'No title';
    const onTogglePin = async () => {
      if (!thread.pinned && pinnedCount >= MAX_PINNED_CHATS) {
        errorToast({ message: PIN_LIMIT_MESSAGE });
        return;
      }
      try {
        await props.updateChatThread(thread.id, { pinned: !thread.pinned });
      } catch (e) {
        const status = e && e.response && e.response.status;
        errorToast({
          message:
            status === 409
              ? PIN_LIMIT_MESSAGE
              : 'Unable to update conversation. Please try again later.',
        });
      }
    };
    return {
      id: thread.id,
      label:
        title.length > 21
          ? `${title.substring(0, 21)}...`
          : title,
      fullTitle: title,
      updatedAt: thread.updatedAt,
      path: `${AI_CHAT}/${thread.id}`,
      pinned: thread.pinned,
      onTogglePin,
      menuOpen: menuOpenId === thread.id,
      onMenuOpenChange: (open) => {
        setMenuOpenId(open ? thread.id : null);
        if (open) setEditingId(null); // opening a menu cancels any other row's edit (D6)
      },
      isEditing: editingId === thread.id,
      onRenameCancel: () => setEditingId(null),
      renameInitialValue: thread.title || '',
      renameMaxLength: 128,
      validateRename: validateThreadTitle,
      onRenameSave: async (newTitle) => {
        try {
          await props.updateChatThread(thread.id, { title: newTitle });
          setEditingId(null);
        } catch (e) {
          errorToast({
            message: 'Unable to update conversation. Please try again later.',
          });
        }
      },
      contextMenuItems: [
        {
          key: 'rename',
          text: 'Rename',
          icon: 'edit',
          onClick: () => setEditingId(thread.id),
        },
        {
          key: 'pin',
          text: thread.pinned ? 'Unpin' : 'Pin',
          icon: thread.pinned ? 'unpin' : 'pin',
          onClick: onTogglePin,
        },
        { key: 'div', divider: true },
        {
          key: 'delete',
          text: 'Delete',
          icon: 'trash',
          intent: Intent.DANGER,
          onClick: () => setPendingDelete({ kind: 'chat', id: thread.id }),
        },
      ],
    };
  };

  const pinnedThreads = chatThreads.filter((t) => t && t.pinned);
  const recentThreads = chatThreads.filter((t) => t && !t.pinned);

  const pinnedChats: MenuItemT = {
    label: 'Pinned Chats',
    icon: 'pin',
    path: AI_CHAT,
    isAccordion: true,
    children: pinnedThreads.map(buildThreadItem),
  };
  const recentChats: MenuItemT = {
    label: 'Recent Chats',
    icon: 'history',
    path: AI_CHAT,
    isAccordion: true,
    children: recentThreads.map(buildThreadItem),
  };

  const createReportWithAI: MenuItemT = {
    label: 'Report Designer',
    icon: 'draw',
    path: AI_REPORTS,
  };

  const validateReportName = (raw) => {
    const trimmed = String(raw || '').trim();
    if (!trimmed) return 'Report name is required';
    if (trimmed.length > REPORT_NAME_MAX_LEN) {
      return 'Report name must be 50 characters or fewer';
    }
    return null;
  };

  // Build accordion from pre-sorted reports (sorting is done in selectors - single source of truth)
  const buildReportsAccordion = (
    label,
    icon,
    basePath,
    reports,
    isOrg,
    prependItems = []
  ) => ({
    label,
    icon,
    path: basePath,
    isAccordion: true,
    children: [
      ...prependItems,
      ...(reports || []).map((report) => {
        const reportName = report.name ? report.name : 'Missing Name';
        const itemLabel =
          report.status === 'draft' ? `[DRAFT] ${reportName}` : reportName;
        return {
          id: report.id,
          label: itemLabel,
          fullTitle: itemLabel,
          path: `${basePath}/${report.id || ''}`,
          menuOpen: menuOpenId === report.id,
          onMenuOpenChange: (open) => {
            setMenuOpenId(open ? report.id : null);
            if (open) setEditingId(null); // opening a menu cancels any other row's edit (D6)
          },
          isEditing: editingId === report.id,
          onRenameCancel: () => setEditingId(null),
          renameInitialValue: report.name || '',
          renameMaxLength: REPORT_NAME_MAX_LEN,
          validateRename: validateReportName,
          onRenameSave: async (newTitle) => {
            try {
              await props.updateDashboardReport(report.id, {
                name: newTitle,
                description: report.description,
                config: report.config,
              });
              setEditingId(null);
            } catch (e) {
              // updateDashboardReport already toasted (409 → duplicate-name);
              // stay in edit mode (D8).
            }
          },
          contextMenuItems: [
            {
              key: 'rename',
              text: 'Rename',
              icon: 'edit',
              disabled: isOrg && !orgReportPerms.canUpdate,
              onClick: () => setEditingId(report.id),
            },
            {
              key: 'edit',
              text: 'Edit',
              icon: 'draw',
              disabled: isOrg && !orgReportPerms.canUpdate,
              onClick: () =>
                props.history.push({
                  pathname: AI_REPORTS_EDIT.replace(':reportId', report.id),
                  state: { from: props.history.location.pathname },
                }),
            },
            { key: 'div', divider: true },
            {
              key: 'delete',
              text: 'Delete',
              icon: 'trash',
              intent: Intent.DANGER,
              disabled: isOrg && !orgReportPerms.canDelete,
              onClick: () =>
                setPendingDelete({ kind: 'report', id: report.id, isOrg }),
            },
          ],
        };
      }),
    ],
  });

  const orgReports = buildReportsAccordion('Organisation Reports', 'clipboard', REPORTS, props.orgReports || [], true);
  const myReports = buildReportsAccordion(
    'My Reports',
    'pie-chart',
    MY_REPORTS,
    props.myReports || [],
    false,
  );
  const explorer: MenuItemT = {
    label: 'Explorer',
    icon: 'series-search',
    path: EXPLORER,
  };
  const heatmaps: MenuItemT = {
    label: 'Heatmaps',
    icon: 'heatmap',
    path: HEATMAPS,
  };
  const campaigns: MenuItemT = {
    label: 'Campaign Manager',
    icon: 'lab-test',
    path: CAMPAIGNS,
  };
  const cameraPreview: MenuItemT = {
    label: 'Camera Preview',
    icon: 'camera',
    path: CAMERA_PREVIEW,
  };
  const headcountPlanner: MenuItemT = {
    label: 'Headcount Planner',
    icon: 'people',
    path: HEADCOUNT_PLANNER,
  };
  const liveOcc: MenuItemT = {
    label: 'Live Occupancy',
    icon: 'eye-open',
    path: LIVE_OCC,
  };
  const settings: MenuItemT = {
    label: 'Admin',
    icon: 'cog',
    path: SETTINGS_MANAGE_LOCATIONS,
    isAccordion: true,
    children: [
      {
        label: 'Locations',
        path: SETTINGS_MANAGE_LOCATIONS,
        alwaysExpanded: true,
      },
      {
        label: 'Users',
        path: SETTINGS_USERS,
      },
      {
        label: 'Tools',
        path: SETTINGS_TOOLS,
      },
    ],
  };

  if (!props.canViewUsers && settings.children) {
    settings.children = settings.children.filter(
      (c) => c.path !== SETTINGS_USERS,
    );
  }

  if (
    (window.location.pathname.includes(`${SETTINGS_MANAGE_SINGLE_LOCATION}/`) ||
      window.location.pathname.includes(SETTINGS_MANAGE_SINGLE_RECORDING)) &&
    settings.children
  ) {
    const settingsLocations = settings.children.find(
      (c) => c.path === SETTINGS_MANAGE_LOCATIONS,
    );
    if (
      settingsLocations &&
      props.location &&
      props.location.state &&
      props.location.state.location &&
      props.location.state.location.name &&
      props.location.state.location.id
    ) {
      settingsLocations.children = [
        {
          label: props.location.state.location.name,
          path: `${SETTINGS_MANAGE_SINGLE_LOCATION}/${props.location.state.location.id}`,
          alwaysExpanded: true,
        },
      ];
    }
  }

  if (
    window.location.pathname.includes(SETTINGS_MANAGE_SINGLE_RECORDING) &&
    settings.children
  ) {
    const settingsLocations = settings.children.find(
      (c) => c.path === SETTINGS_MANAGE_LOCATIONS,
    );
    if (settingsLocations && settingsLocations.children) {
      const settingsLocation = settingsLocations.children.find((c) =>
        c.path.includes(SETTINGS_MANAGE_SINGLE_LOCATION),
      );
      if (
        settingsLocation &&
        props.location &&
        props.location.state &&
        props.location.state.recording &&
        props.location.state.recording.name &&
        props.location.state.recording.id
      ) {
        settingsLocation.children = [
          {
            label: props.location.state.recording.name,
            path: `${SETTINGS_MANAGE_SINGLE_RECORDING}/${props.location.state.recording.id}`,
          },
        ];
      }
    }
  }

  const training: MenuItemT = {
    label: 'Training',
    icon: 'learning',
    path: TRAINING,
  };
  const divider = {
    type: 'divider',
  };

  const productMenuItems = [
    home,
    aiChat,
    pinnedChats,
    recentChats,
    createReportWithAI,
    myReports,
    orgReports,
    divider,
    keyMetrics,
    explorer,
    divider,
    campaigns,
    divider,
    heatmaps,
    cameraPreview,
    divider,
    training,
    divider,
    settings,
  ];
  const productMenuItemsMobile = [
    home,
    aiChat,
    pinnedChats,
    recentChats,
    createReportWithAI,
    myReports,
    orgReports,
    keyMetrics,
    explorer,
    campaigns,
    heatmaps,
    cameraPreview,
    training,
    settings,
  ];

  if (props.showHeadcountPlanner) {
    productMenuItems.splice(8, 0, headcountPlanner);
    productMenuItemsMobile.splice(7, 0, headcountPlanner);
  }
  if (props.showLivePage) {
    let desktopIndex = 7;
    let mobileIndex = 7;
    if (productMenuItems.includes(headcountPlanner, 5)) {
      desktopIndex++;
      mobileIndex++;
    }
    productMenuItems.splice(desktopIndex, 0, liveOcc);
    productMenuItemsMobile.splice(mobileIndex, 0, liveOcc);
  }

  // Override logout destination if the theme specifies it
  // This URL must be in the "Allowed Logout URLs" in the Auth0 dashboard
  const theme = useContext(ThemeContext);
  let logout = props.logout;
  if (theme && theme.overrides && theme.overrides.logout) {
    logout = () => props.logout({ returnTo: theme.overrides.logout });
  }

  // Add any extra menu items from the theme
  if (theme && theme.overrides && theme.overrides.menuItems) {
    theme.overrides.menuItems.forEach((item) => {
      const { label, icon, path } = item;
      const menuItem = { label, icon, path };

      if (item.position === 'top') {
        productMenuItems.unshift(menuItem);
        productMenuItemsMobile.unshift(menuItem);
      } else {
        productMenuItems.push(menuItem);
        productMenuItemsMobile.push(menuItem);
      }
    });
  }

  const confirmPendingDelete = async () => {
    const target = pendingDelete;
    setPendingDelete(null);
    if (!target) return;
    const currentPath = props.history.location.pathname;

    if (target.kind === 'chat') {
      try {
        await props.deleteChatThread(target.id);
      } catch (e) {
        errorToast({
          message: 'Unable to delete conversation. Please try again later.',
        });
        return;
      }
      if (currentPath === `${AI_CHAT}/${target.id}`) props.history.push(AI_CHAT);
      return;
    }

    // report — precompute the next-in-scope target BEFORE deleting.
    const scopeReports =
      (target.isOrg ? props.orgReports : props.myReports) || [];
    const nextReport = scopeReports.find((r) => r && r.id !== target.id);
    try {
      await props.deleteDashboardReport(target.id);
    } catch (e) {
      errorToast({ message: 'Unable to delete report. Please try again later.' });
      return;
    }
    successToast({ message: 'Report deleted.' });
    const targetId = String(target.id || '');
    const viewingRoutes = [
      `${target.isOrg ? REPORTS : MY_REPORTS}/${targetId}`,
      `${AI_REPORTS}/${targetId}`,
    ];
    if (viewingRoutes.includes(currentPath)) {
      if (nextReport) {
        const single = target.isOrg ? SINGLE_REPORT : SINGLE_MY_REPORT;
        props.history.push(single.replace(':reportId', nextReport.id));
      } else {
        props.history.push(target.isOrg ? REPORTS : MY_REPORTS);
      }
    }
  };

  const isChatDelete = pendingDelete && pendingDelete.kind === 'chat';
  const isOrgReportDelete =
    pendingDelete && pendingDelete.kind === 'report' && pendingDelete.isOrg;

  return (
    <Fragment>
      <Alert
        cancelButtonText="Cancel"
        confirmButtonText={isChatDelete ? 'Delete' : 'Delete Report'}
        icon="trash"
        intent={Intent.DANGER}
        isOpen={!!pendingDelete}
        onCancel={() => setPendingDelete(null)}
        onConfirm={confirmPendingDelete}
      >
        {isChatDelete ? (
          <p>Are you sure you want to delete this conversation?</p>
        ) : (
          <p>
            {isOrgReportDelete
              ? 'Deleting this report will delete it from the Organisation Reports menu for all of the users within your company. Are you sure you want to delete this report?'
              : 'Are you sure you want to delete this report?'}
          </p>
        )}
      </Alert>
      <Mobile className={'sidebar'}>
        <MobileSidebar
          {...props}
          logout={logout}
          productMenuItems={productMenuItemsMobile}
        />
      </Mobile>
      <Desktop className={'sidebar'}>
        <DesktopSidebar
          {...props}
          logout={logout}
          productMenuItems={productMenuItems}
        />
      </Desktop>
    </Fragment>
  );
};

export default withRouter(Sidebar);
